import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { fmtId, leesbareFout } from '../util/format';
import DropZone from '../components/DropZone';
import MeldingFormulier, { datumUitKenmerk, datumNL } from '../components/MeldingFormulier';
import {
  getInrichtingDetail, getHouderDetail, importMeldingPreview, importMelding, getPutten,
  getGebieden, getGeplandeMelding, wijzigGeplandeMelding, trekGeplandeMeldingIn,
} from '../api/api';
import {
  puttenUitPreview, waardenUitPreview, puttenUitInrichting, bouwImportPayload,
  waardenUitPayload, puttenUitPayload,
} from '../components/meldingModel';

/* Nieuwe melding verwerken — een pagina, geen modal.

   Dit scherm zat in een venster over de inrichtingspagina heen. Dat werkt voor
   een vraag met twee knoppen, niet voor een taak waarbij je een kaart leest,
   putten aanwijst en velden invult: het venster kon nooit hoger worden dan het
   scherm, dus zat er een scrollbalk ín het venster terwijl de pagina eronder
   ook al scrolde, en de kaart hield nauwelijks ruimte over.

   Twee wegen, net als voorheen:

     Via bijlage   een meldings-PDF inlezen. Wat de melder heeft opgegeven staat
                   vast; je vult alleen het kenmerk in.
     Handmatig     de melding zelf opvoeren, bijvoorbeeld na een papieren
                   melding. Dan is alles invulbaar en kun je putten toevoegen. */

/* leesbareFout woont in util/format: de pagina "Nieuwe put" leest sinds de
   PDF-import dezelfde 422-antwoorden van dezelfde endpoints, en twee kopieën
   van dezelfde vertaling lopen vroeg of laat uiteen. */

const LEEG = {
  kenmerkType: 'verzoeknummer', kenmerk: '', meldingsdatum: '',
  doel: '', pompcapaciteit: '', hectare: '',
};

export default function MeldingVerwerken() {
  /* Twee ingangen naar dezelfde pagina:

       /beregening/inrichtingen/:id/melding-verwerken     een nieuwe melding op
                                                          een bestaande inrichting
       /beregening/houders/:houderId/melding-verwerken    de eerste melding van
                                                          een houder

     In het tweede geval is er geen inrichting: het register krijgt er een bij
     zodra de melding wordt verwerkt. De houder komt dan uit de route. */
  const { id, houderId } = useParams();
  const zonderInrichting = !id;
  const navigate = useNavigate();
  /* ?gepland=<id> — dan bewerk je een melding die al is verwerkt maar nog niet
     is ingegaan. Er staat niets van in het register, dus alles wat je ziet komt
     uit de bewaarde payload en gaat er straks weer als geheel in terug. */
  const [zoek] = useSearchParams();
  const geplandId = zoek.get('gepland');

  const [detail, setDetail] = useState(null);
  const [laadFout, setLaadFout] = useState(null);

  const [weg, setWeg] = useState(geplandId ? 'handmatig' : 'bijlage');  // 'bijlage' | 'handmatig'
  const [stap, setStap] = useState('start');        // 'start' | 'formulier'
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState(null);

  const [klaargezet, setKlaargezet] = useState(null);  // { inwerkingtreding } bij een melding die pas later ingaat
  const [bron, setBron] = useState(null);           // { bestand, herkomst }
  const [waarden, setWaarden] = useState(LEEG);
  const [putten, setPutten] = useState([]);
  const [registerPutten, setRegisterPutten] = useState([]);
  const [gebieden, setGebieden] = useState([]);
  const [geplandRij, setGeplandRij] = useState(null);   // de klaarstaande melding die je bekijkt
  /* Lezen doe je in het venster op de inrichtingpagina; kom je hier, dan kom je
     om te wijzigen. Vandaar meteen de bewerkstand. */
  const [bekijken, setBekijken] = useState(false);

  useEffect(() => {
    if (!geplandId) return;
    getGeplandeMelding(geplandId)
      .then((r) => {
        setGeplandRij(r.data);
        setWaarden(waardenUitPayload(r.data.payload));
        setPutten(puttenUitPayload(r.data.payload));
        setWeg('handmatig');
        setStap('formulier');
      })
      .catch((e) => setLaadFout(leesbareFout(e)));
  }, [geplandId]);

  useEffect(() => {
    const halen = id
      ? getInrichtingDetail(id).then((r) => r.data)
      : getHouderDetail(houderId).then((r) => ({ inrichting: null, houder: r.data.houder, putten: [] }));
    halen.then(setDetail).catch((e) => setLaadFout(leesbareFout(e)));
    getPutten()
      .then((r) => setRegisterPutten((r.data || []).filter((p) => !p.buiten_gebruik_datum)))
      .catch(() => {});
    // Eigen cache van 5 minuten in de api-laag, dus doorgaans geen netwerkcall.
    getGebieden(undefined, true).then((r) => setGebieden(r.data || [])).catch(() => {});
  }, [id, houderId]);

  const terug = useCallback(
    () => navigate(id ? `/beregening/inrichtingen/${id}` : `/beregening/houders/${houderId}`),
    [navigate, id, houderId],
  );

  const meldingsdatum = waarden.kenmerkType === 'verzoeknummer'
    ? datumUitKenmerk(waarden.kenmerk)
    : waarden.meldingsdatum;

  if (laadFout) {
    return (
      <div className="main-inner">
        <div className="alert alert-error" style={{ margin: '0 32px' }}>{laadFout}</div>
      </div>
    );
  }
  if (!detail) {
    return <div className="empty-state"><span className="spinner" /> Laden…</div>;
  }

  const inrichting = detail.inrichting;
  const houder = detail.houder;
  const actievePutten = (detail.putten || []).filter((p) => p.actief !== false);

  // --- Via bijlage: PDF inlezen ---
  const leesPdf = async (file) => {
    if (!file.name.toLowerCase().endsWith('.pdf')) { setFout('Alleen PDF-bestanden.'); return; }
    setFout(null);
    setBezig(true);
    try {
      const formData = new FormData();
      formData.append('pdf', file);
      const res = await importMeldingPreview(formData);
      setWaarden(waardenUitPreview(res.data));
      setPutten(puttenUitPreview(res.data));
      setBron({ bestand: file.name, herkomst: res.data.bron === 'tekstlaag' ? 'tekst' : 'json' });
      setStap('formulier');
    } catch (e) {
      setFout(leesbareFout(e));
    } finally {
      setBezig(false);
    }
  };

  // --- Handmatig: startkeuze ---
  const beginOvernemen = () => {
    setWaarden({
      ...LEEG,
      kenmerkType: 'zaaknummer',
      doel: inrichting.doel || '',
      pompcapaciteit: inrichting.max_pompcap_m3_uur != null ? String(inrichting.max_pompcap_m3_uur) : '',
      hectare: inrichting.bedrijfsomvang_ha != null ? String(inrichting.bedrijfsomvang_ha) : '',
    });
    setPutten(puttenUitInrichting(actievePutten));
    setStap('formulier');
  };
  const beginLeeg = () => {
    setWaarden({ ...LEEG, kenmerkType: 'zaaknummer' });
    setPutten([]);
    setStap('formulier');
  };

  const opslaan = async () => {
    setBezig(true);
    setFout(null);
    try {
      const body = bouwImportPayload({
        houderId: inrichting ? inrichting.houder_id : houderId, waarden, meldingsdatum, putten,
      });
      const res = geplandId
        ? await wijzigGeplandeMelding(geplandId, body)
        : await importMelding(body);
      /* Een melding gaat 28 dagen na de meldingsdatum in. Tot dat moment blijft
         het lopende recht staan en verandert er in het register niets — dus
         terugspringen naar een inrichtingspagina die er precies hetzelfde
         uitziet zou lezen als "er is niets gebeurd". Vandaar deze bevestiging. */
      if (res?.data?.status === 'klaargezet') {
        setKlaargezet({ inwerkingtreding: res.data.inwerkingtreding });
        return;
      }
      terug();
    } catch (e) {
      setFout(leesbareFout(e));
    } finally {
      setBezig(false);
    }
  };

  const intrekken = async () => {
    if (!window.confirm(
      `Weet je zeker dat je de klaarstaande melding ${waarden.kenmerk} wilt intrekken? `
      + 'Er is nog niets in het register veranderd; het lopende gebruiksrecht blijft staan.'
    )) return;
    setBezig(true);
    setFout(null);
    try {
      await trekGeplandeMeldingIn(geplandId);
      terug();
    } catch (e) {
      setFout(leesbareFout(e));
      setBezig(false);
    }
  };

  const houderNaam = houder
    ? (houder.naam || [houder.voornaam, houder.tussenvoegsel, houder.achternaam].filter(Boolean).join(' ') || null)
    : null;

  const wissel = (naar) => {
    setWeg(naar); setStap('start'); setFout(null);
    setBron(null); setPutten([]); setWaarden(LEEG);
  };

  const tabStijl = (actief) => ({
    padding: '8px 16px', cursor: 'pointer', border: 'none',
    borderBottom: actief ? '2px solid var(--accent)' : '2px solid transparent',
    background: 'transparent', fontWeight: actief ? 600 : 400,
    color: actief ? 'var(--text)' : 'var(--text-muted)', fontSize: 14,
  });

  return (
    <div className="main-inner main-inner-wide">
      <div className="page-header">
        <div>
          <p className="page-subtitle" style={{ marginBottom: 4 }}>
            {zonderInrichting
              ? <Link to={`/beregening/houders/${houderId}`}>← {houderNaam || fmtId(houderId)}</Link>
              : <Link to={`/beregening/inrichtingen/${id}`}>← Inrichting {fmtId(id)}</Link>}
          </p>
          <h1 className="page-title">
            {geplandId ? (bekijken ? 'Klaarstaande melding' : 'Klaarstaande melding bewerken')
              : zonderInrichting ? 'Melding verwerken' : 'Nieuwe melding verwerken'}
          </h1>
          <p className="page-subtitle">
            {houderNaam} <span className="mono">{fmtId(inrichting ? inrichting.houder_id : houderId)}</span>
          </p>
        </div>
        {/* De weg waarlangs de melding binnenkomt bepaalt wat je op deze pagina
            mag: bij een bijlage staat alles vast. Dat hoort dus in de paginakop
            en niet ergens halverwege het formulier. */}
        {geplandId ? (
          geplandRij && (
            <span className="mf-badge is-klaarstaand">
              Gaat in op {datumNL(geplandRij.inwerkingtreding)}
            </span>
          )
        ) : stap === 'formulier' && (
          <span className={`mf-badge ${weg === 'bijlage' ? 'is-bijlage' : ''}`}>
            {weg === 'bijlage' ? 'Via bijlage' : 'Handmatig'}
          </span>
        )}
      </div>

      {klaargezet && (
        <div className="card mf-kaart">
          <h2 style={{ margin: '0 0 6px', fontSize: 19, fontWeight: 600, letterSpacing: '-0.01em' }}>
            {geplandId ? 'Klaarstaande melding bijgewerkt' : 'Melding klaargezet'}
          </h2>
          <p style={{ margin: '0 0 4px', fontSize: 14, color: 'var(--text-soft)', lineHeight: 1.55 }}>
            Deze melding gaat in op <strong>{datumNL(klaargezet.inwerkingtreding)}</strong>. Tot die dag
            blijft het lopende gebruiksrecht staan, met de putten die er nu bij horen.
          </p>
          <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--text-soft)', lineHeight: 1.55 }}>
            Er is dus nog niets aan het register veranderd. Op de ingangsdatum wordt de melding
            vanzelf verwerkt.
          </p>
          <button className="btn btn-primary" onClick={terug}>
            {zonderInrichting ? 'Terug naar de houder' : 'Terug naar de inrichting'}
          </button>
        </div>
      )}

      {!klaargezet && stap === 'start' && geplandId && (
        <div className="empty-state"><span className="spinner" /> Klaarstaande melding laden…</div>
      )}

      {!klaargezet && stap === 'start' && !geplandId && (
        <>
          <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--separator)', marginBottom: 24 }}>
            <button style={tabStijl(weg === 'bijlage')} onClick={() => wissel('bijlage')}>Via bijlage</button>
            <button style={tabStijl(weg === 'handmatig')} onClick={() => wissel('handmatig')}>Handmatig</button>
          </div>

          {fout && <div className="alert alert-error" style={{ marginBottom: 16 }}>{fout}</div>}

          {weg === 'bijlage' ? (
            bezig ? (
              <div className="empty-state"><span className="spinner" /> Bijlage wordt gelezen…</div>
            ) : (
              <DropZone
                accept="application/pdf"
                onFile={leesPdf}
                label="Sleep de bijlage van het meldingsformulier hierheen"
                hint="of klik om een bestand te kiezen"
              />
            )
          ) : (
            <div className="card mf-kaart">
              <p className="mf-start-vraag">Waar wil je mee beginnen?</p>
              <div className="mf-start-keuzes">
                {/* Is er nog geen inrichting, dan valt er ook niets over te
                    nemen; dan blijft alleen leeg beginnen over. */}
                {!zonderInrichting && (
                <button className="mf-start-kaart" onClick={beginOvernemen}>
                  <span className="mf-start-titel">De huidige registratie overnemen</span>
                  <span className="mf-start-sub">
                    Doel, pompcapaciteit, bedrijfsomvang en de {actievePutten.length}{' '}
                    gekoppelde {actievePutten.length === 1 ? 'put' : 'putten'} worden ingeladen.
                  </span>
                </button>
                )}
                <button className="mf-start-kaart" onClick={beginLeeg}>
                  <span className="mf-start-titel">Leeg beginnen</span>
                  <span className="mf-start-sub">Alles zelf invullen.</span>
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {!klaargezet && stap === 'formulier' && (
        <MeldingFormulier
          modus={geplandId && bekijken ? 'bekijken' : (weg === 'bijlage' ? 'controleren' : 'bewerken')}
          bron={bron}
          waarden={waarden}
          onChange={setWaarden}
          putten={putten}
          onPuttenChange={setPutten}
          registerPutten={registerPutten}
          gebieden={gebieden}
          houder={null}
          inrichtingKenmerk={null}
          badge={false}
          titel={null}
          primair={geplandId ? (bekijken ? 'Bewerken' : 'Klaarstaande melding bijwerken')
            : zonderInrichting ? 'Melding registreren' : 'Inrichting bijwerken'}
          onAnnuleer={terug}
          onOpslaan={geplandId && bekijken ? () => setBekijken(false) : opslaan}
          extraActie={geplandId && bekijken
            ? { label: 'Melding intrekken', onClick: intrekken, gevaarlijk: true }
            : null}
          bezig={bezig}
          fout={fout}
        />
      )}
    </div>
  );
}
